import React, { useState, useEffect } from 'react';
import { API } from '../App';
import axios from 'axios';
import { Landmark, CreditCard, Calendar, Loader2, PlusCircle, AlertCircle } from 'lucide-react';

const fmt = (n) => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(n || 0);

const DeudorDashboard = ({ user }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [requestingNew, setRequestingNew] = useState(false);

  useEffect(() => {
    axios.get(`${API}/deudor/my-debts`, { withCredentials: true })
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleRequestNew = () => {
    setRequestingNew(true);
    // Redirect to application form
    window.location.href = '/deudor-apply';
  };

  if (loading) return <div className="min-h-screen bg-[#0f1724] flex items-center justify-center"><Loader2 className="animate-spin text-[#D4AF37]" size={40} /></div>;

  const loans = data?.loans || [];
  const totalDebt = loans.reduce((s, l) => s + (l.monto || 0), 0);
  const activeLoans = loans.filter(l => l.estado === 'activo');

  return (
    <div className="min-h-screen bg-[#0f1724] p-4 md:p-8" data-testid="deudor-dashboard">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
              Mis Préstamos
            </h1>
            <p className="text-gray-400 text-sm">{user?.name} — {user?.email}</p>
          </div>
          <button onClick={handleRequestNew}
            className="flex items-center gap-2 bg-[#D4AF37] text-[#141b2d] px-4 py-2 rounded-lg font-medium hover:bg-[#c29d2e] transition-colors text-sm"
            data-testid="request-new-loan">
            <PlusCircle size={16} /> Solicitar Préstamo
          </button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <div className="bg-[#1a2332] border border-[#2a3444] rounded-xl p-5">
            <CreditCard className="text-red-400 mb-2" size={24} />
            <div className="text-2xl font-bold text-white">{fmt(totalDebt)}</div>
            <div className="text-sm text-gray-400">Deuda Total</div>
          </div>
          <div className="bg-[#1a2332] border border-[#2a3444] rounded-xl p-5">
            <Landmark className="text-[#D4AF37] mb-2" size={24} />
            <div className="text-2xl font-bold text-white">{activeLoans.length}</div>
            <div className="text-sm text-gray-400">Préstamos Activos</div>
          </div>
          <div className="bg-[#1a2332] border border-[#2a3444] rounded-xl p-5">
            <Calendar className="text-blue-400 mb-2" size={24} />
            <div className="text-2xl font-bold text-white">{loans.length}</div>
            <div className="text-sm text-gray-400">Total Préstamos</div>
          </div>
        </div>

        {/* Loans */}
        {loans.length === 0 ? (
          <div className="bg-[#1a2332] border border-[#2a3444] rounded-xl p-12 text-center">
            <AlertCircle className="mx-auto mb-4 text-gray-600" size={48} />
            <p className="text-gray-400">No tienes préstamos registrados.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {loans.map(loan => (
              <div key={loan.id} className="bg-[#1a2332] border border-[#2a3444] rounded-xl overflow-hidden">
                <div className="p-5 border-b border-[#2a3444] flex items-center justify-between">
                  <div>
                    <span className="font-mono text-[#D4AF37] font-bold">{loan.consecutivo}</span>
                    <span className={`ml-3 px-2 py-0.5 rounded-full text-xs ${
                      loan.estado === 'activo' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-blue-500/20 text-blue-400'
                    }`}>{loan.estado}</span>
                  </div>
                  <div className="text-right">
                    <div className="text-white font-bold">{fmt(loan.monto)}</div>
                    <div className="text-xs text-gray-500">{loan.tasa_interes}% — {loan.periodo_pago}</div>
                  </div>
                </div>
                
                {/* Amortization Table */}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-[#141b2d]">
                      <tr>
                        {['#', 'Fecha', 'Capital', 'Interés', 'Total Cuota', 'Pagado', 'Saldo', 'Estado'].map(h => (
                          <th key={h} className="px-3 py-2 text-left text-gray-500 font-medium text-xs">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {(loan.detalles || []).map(d => (
                        <tr key={d.id} className={`border-t border-[#2a3444]/50 ${
                          d.estado === 'pagado' ? 'bg-emerald-500/5' : ''
                        }`}>
                          <td className="px-3 py-2 text-white">{d.numero_cuota}</td>
                          <td className="px-3 py-2 text-gray-400">{d.fecha_programada}</td>
                          <td className="px-3 py-2 text-white">{fmt(d.capital)}</td>
                          <td className="px-3 py-2 text-white">{fmt(d.interes)}</td>
                          <td className="px-3 py-2 text-white font-medium">{fmt(d.total_cuota)}</td>
                          <td className="px-3 py-2 text-emerald-400">{fmt((d.pagado_capital || 0) + (d.pagado_interes || 0))}</td>
                          <td className="px-3 py-2 text-gray-400">{fmt(d.saldo_restante)}</td>
                          <td className="px-3 py-2">
                            <span className={`px-2 py-0.5 rounded-full text-xs ${
                              d.estado === 'pagado' ? 'bg-emerald-500/20 text-emerald-400' :
                              d.estado === 'parcial' ? 'bg-yellow-500/20 text-yellow-400' :
                              'bg-gray-500/20 text-gray-400'
                            }`}>{d.estado}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DeudorDashboard;
